import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import Logo from "@/components/Logo";

export default function Header() {
  const { t, lang, switchLang } = useI18n();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const base = `/${lang}`;
  const nav = [
    { href: `${base}#opportunities`, label: t("nav.opportunities") },
    { href: `${base}#how`, label: t("nav.how_it_works") },
    { href: `${base}#trust`, label: t("nav.trust") },
    { href: `${base}#contact`, label: t("nav.contact") },
  ];

  const goAnchor = (e, href) => {
    // If we are already on the home path, prevent full navigation to keep smooth scroll
    const [path, hash] = href.split("#");
    if (location.pathname === path && hash) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.1 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`no-break fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-slate-900/8" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-[68px] md:h-[76px]">
        <Link
          to={`/${lang}`}
          className="no-break shrink-0"
          data-testid="header-logo"
          aria-label="Ronavirona home"
        >
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={(e) => goAnchor(e, n.href)}
              className="link-underline text-[0.9rem] tracking-tight text-[#0F172A]/85 hover:text-[#0F172A]"
              data-testid={`nav-${n.href.split("#")[1] || "home"}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangSwitch lang={lang} switchLang={switchLang} t={t} />
          <a
            href={`${base}#contact`}
            onClick={(e) => goAnchor(e, `${base}#contact`)}
            className="btn-pill btn-pill--dark"
            data-testid="header-cta"
          >
            {t("hero.cta_secondary")}
            <span aria-hidden>→</span>
          </a>
        </div>

        {/* Mobile: hamburger */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-900/15 text-[#0F172A]"
          onClick={() => setOpen(true)}
          aria-label={t("nav.menu_open")}
          data-testid="mobile-menu-open"
        >
          <Menu size={18} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden fixed inset-0 z-[60] bg-[#FAF9F6]"
            data-testid="mobile-drawer"
          >
            <div className="container-x flex items-center justify-between h-[68px]">
              <Logo />
              <button
                type="button"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-slate-900/15"
                onClick={() => setOpen(false)}
                aria-label={t("nav.menu_close")}
                data-testid="mobile-menu-close"
              >
                <X size={18} />
              </button>
            </div>
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: { transition: { staggerChildren: 0.03 } },
                show: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
              }}
              className="container-x pt-8 pb-10 flex flex-col gap-6"
            >
              {nav.map((n) => (
                <motion.a
                  key={n.href}
                  href={n.href}
                  onClick={(e) => {
                    goAnchor(e, n.href);
                    setOpen(false);
                  }}
                  variants={{
                    hidden: { opacity: 0, y: 18 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.2, 0.7, 0.2, 1] } },
                  }}
                  className="font-serif-display text-4xl tracking-tight"
                  data-testid={`mobile-nav-${n.href.split("#")[1] || "home"}`}
                >
                  {n.label}
                </motion.a>
              ))}
              <div className="rule my-4" />
              <div className="flex items-center gap-3">
                <LangSwitch lang={lang} switchLang={switchLang} t={t} />
                <a
                  href={`${base}#contact`}
                  onClick={(e) => {
                    goAnchor(e, `${base}#contact`);
                    setOpen(false);
                  }}
                  className="btn-pill btn-pill--dark ml-auto"
                  data-testid="mobile-cta"
                >
                  {t("hero.cta_secondary")} <span aria-hidden>→</span>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function LangSwitch({ lang, switchLang, t }) {
  return (
    <div
      role="group"
      aria-label={t("language_switch.aria")}
      className="inline-flex items-center rounded-full border border-slate-900/15 p-0.5 font-mono-mark text-[0.68rem] tracking-[0.2em]"
    >
      <button
        type="button"
        onClick={() => switchLang("ar")}
        className={`px-3 py-1.5 rounded-full transition-colors ${lang === "ar" ? "bg-[#0F172A] text-[#FAF9F6]" : "text-[#0F172A]/70 hover:text-[#0F172A]"}`}
        data-testid="language-switcher-es"
      >
        ES
      </button>
      <button
        type="button"
        onClick={() => switchLang("en")}
        className={`px-3 py-1.5 rounded-full transition-colors ${lang === "en" ? "bg-[#0F172A] text-[#FAF9F6]" : "text-[#0F172A]/70 hover:text-[#0F172A]"}`}
        data-testid="language-switcher-en"
      >
        EN
      </button>
    </div>
  );
}
